import { assert } from '@sama/js-core';
import { difference, keys, values } from '@sama/micro-dash';
import { Camera, OrthographicCamera, RepeatWrapping, Texture } from 'three';
import { ShapeState3D } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { assertDefined } from '../../../cross-team-shared/misc-utils/strict-record';
import { isCuboidLocationState3D } from '../../state/cuboid-location-state-3d';
import { ShapeLocationState3D } from '../../state/shape-location-state-3d';
import { getViewRegionNameForLayer } from '../../state/view-region-state';
import { ObjectWith } from '../../utils/misc-utils';
import { pointToVector } from '../../utils/point-cloud-utils';
import { ConfigureMeshesConfiguration } from './abstract-mesh-helper';
import { getVerticesFromLocation } from './cloud/point-cloud-utils';
import { CuboidHelper } from './cuboid/cuboid-helper';
import { AbstractPolyShapeHelper } from './poly-shape/abstract-polyshape-helper';

interface Disposable {
  dispose: () => void;
}

export function disposeAll(toDispose: ObjectWith<Disposable>) {
  values(toDispose).forEach((t) => t.dispose());
}

export function haveShapeIdsChanged(
  current: ObjectWith<ShapeState3D>,
  previous: ObjectWith<ShapeState3D>,
) {
  const currentIds = keys(current);
  const previousIds = keys(previous);
  return (
    difference(currentIds, previousIds).length === 0 &&
    difference(previousIds, currentIds).length === 0
  );
}

export function getVertices(location: ShapeLocationState3D) {
  // Avoid using geometry positions directly because we could be using
  // thousands of segments due to curved edges. Instead, calculate vertices
  // from interpolated location and then convert them into local space
  // coordinates so that the rest of the math keeps being as close to the
  // shader as possible
  if (isCuboidLocationState3D(location)) {
    return getVerticesFromLocation(location);
  }
  return location.points.map(pointToVector);
}

export const stripedTexture = buildStripedTexture(10);

export function buildStripedTexture(uvSurfaceSize: number) {
  const texture = new Texture(
    buildStripedCanvas(),
    undefined,
    RepeatWrapping,
    RepeatWrapping,
  );
  texture.repeat.set(uvSurfaceSize, uvSurfaceSize);
  texture.needsUpdate = true;

  return texture;
}

export function isCuboidHelper(
  helper: CuboidHelper | AbstractPolyShapeHelper,
): helper is CuboidHelper {
  return helper instanceof CuboidHelper;
}

export function pixelToOrthographicWorldSize(
  pixelSize: number,
  config: ConfigureMeshesConfiguration,
) {
  const { state, cameraLayer, camera } = config;
  assert(
    isOrthographicCamera(camera),
    'should be called only with orthographic cameras',
  );

  const viewRegionName = getViewRegionNameForLayer(cameraLayer);
  const viewRegion = state.viewRegions.regions[viewRegionName];
  assertDefined(viewRegion);

  const [width] = viewRegion.size;
  const { left, right } = camera;
  const worldUnitPerPixel = (right - left) / width;

  const worldUnits = pixelSize * worldUnitPerPixel;
  return worldUnits;
}

function isOrthographicCamera(camera: Camera): camera is OrthographicCamera {
  return camera.type === 'OrthographicCamera';
}

function buildStripedCanvas() {
  const size = 32;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const context = canvas.getContext('2d')!;
  const spacing = size / 2;

  context.strokeStyle = 'white';
  context.lineWidth = Math.hypot(spacing / 2, spacing / 2);
  context.lineCap = 'square';
  context.beginPath();
  for (let i = 0; i < 2; i++) {
    context.moveTo(i * size - spacing, 0);
    context.lineTo(i * size + spacing, size);
  }
  context.stroke();

  return canvas;
}
